import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { deletePlan } from 'api/transactionPlans';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';
import { accountQueryKeys } from 'queries/account-query-keys';
import { TransactionPlan } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface CancelRecurringPlanAlertDialogProps extends AlertDialog.AlertDialogRootProps {
    plan: TransactionPlan;
}

interface CancelRecurringPlanFormValues {
    cancel_reason: string;
}

const CancelRecurringPlanAlertDialog = ({
    plan,
    children,
    ...rest
}: CancelRecurringPlanAlertDialogProps) => {
    const toast = useToast();
    const { key, forceRender } = useForceRender();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();

    const methods = Form.useForm<CancelRecurringPlanFormValues>({
        defaultValues: {
            cancel_reason: '',
        },
    });

    const { control, handleSubmit, path } = methods;

    const { mutate, isPending } = useMutation<
        unknown,
        GivebutterAxiosError,
        CancelRecurringPlanFormValues
    >({
        mutationFn: values => deletePlan(accountId, plan.id, values),
        onSuccess: () => {
            toast.success('Plan canceled!');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.plans(accountId) });
            forceRender();
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data?.message ?? 'Unable to cancel plan');
        },
    });

    return (
        <AlertDialog.Root type="error" key={key} {...rest}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <Form.Provider {...methods}>
                        <form
                            onSubmit={handleSubmit(values => mutate(values))}
                            className="contents"
                        >
                            <AlertDialog.Header>
                                <AlertDialog.Icon />
                                <AlertDialog.Close />
                            </AlertDialog.Header>
                            <AlertDialog.Body>
                                <AlertDialog.Title>Cancel plan</AlertDialog.Title>
                                <AlertDialog.Description>
                                    Are you sure? You can't undo this action afterwards.
                                </AlertDialog.Description>
                            </AlertDialog.Body>
                            <Form.Container>
                                <Form.Label name={path('cancel_reason')}>
                                    Cancellation reason
                                </Form.Label>
                                <Form.SingleSelect
                                    control={control}
                                    name="cancel_reason"
                                    rules={{ required: 'Cancellation reason is required.' }}
                                >
                                    <SingleSelect.Trigger aria-label="Cancel reason">
                                        <SingleSelect.Value placeholder="Select a reason" />
                                    </SingleSelect.Trigger>
                                    <SingleSelect.Content className="w-[--radix-select-trigger-width]">
                                        <SingleSelect.Item value="SIGNUP_BY_ACCIDENT">
                                            <SingleSelect.ItemText>
                                                Donor signed up for a recurring plan by accident.
                                            </SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="CHANGE_CAMPAIGN">
                                            <SingleSelect.ItemText>
                                                Donor plans to give to a different initiative in the
                                                same organization.
                                            </SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="DONATED_ENOUGH">
                                            <SingleSelect.ItemText>
                                                Donor feels like they've donated enough.
                                            </SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="NO_LONGER_SUPPORT">
                                            <SingleSelect.ItemText>
                                                Donor doesn't want to support this organization
                                                anymore.
                                            </SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="NOT_THANKED">
                                            <SingleSelect.ItemText>
                                                The organization didn't appropriately thank the
                                                donor.
                                            </SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="NOT_SURE_IF_MATTERS">
                                            <SingleSelect.ItemText>
                                                Donor has no idea how the gift is being used or if
                                                their gift really matters.
                                            </SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="UNREQUESTED_INFO">
                                            <SingleSelect.ItemText>
                                                The organization is providing donor with unrequested
                                                information about the programs they're supporting.
                                            </SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="REASON_NOT_UNDERSTOOD">
                                            <SingleSelect.ItemText>
                                                Donor feels like the nonprofit does not understand
                                                why they made the gift in the first place.
                                            </SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="CAN_NOT_AFFORD">
                                            <SingleSelect.ItemText>
                                                Donor can no longer afford to give.
                                            </SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="OTHER">
                                            <SingleSelect.ItemText>
                                                Donor stopped giving for another reason.
                                            </SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                    </SingleSelect.Content>
                                </Form.SingleSelect>
                                <Form.Error name={path('cancel_reason')} />
                            </Form.Container>
                            <AlertDialog.Footer>
                                <AlertDialog.Cancel className="flex-1">Cancel</AlertDialog.Cancel>
                                <Form.SubmitButton
                                    variant="solid"
                                    color="error"
                                    isSubmitting={isPending}
                                    className="flex-1"
                                >
                                    Cancel plan
                                </Form.SubmitButton>
                            </AlertDialog.Footer>
                        </form>
                    </Form.Provider>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const CancelRecurringPlanAlertDialogTrigger = AlertDialog.Trigger;

const Root = CancelRecurringPlanAlertDialog;
const Trigger = CancelRecurringPlanAlertDialogTrigger;

export {
    CancelRecurringPlanAlertDialog,
    CancelRecurringPlanAlertDialogTrigger,
    //
    Root,
    Trigger,
};
